import React, { FC } from "react";
import { useSelector } from "react-redux";

import { Icon } from "../../common";
import { useInvoiceCurrencies } from "../../../hooks/useInvoiceCurrencies";
import { selectInvoice } from "../../../store/reducers/invoice";
import { InvoiceStatus } from "../../../types/InvoiceTypes";
import { formatAmount } from "../../../utils/numbers/money";
import { formatDate, DateFormat } from "../../../utils/date";

const paidAmountLabels = {
  [InvoiceStatus.PENDING]: "Payer is going to send",
  [InvoiceStatus.PENDING_REVIEW]: "Payer is going to send",
  [InvoiceStatus.PROCESSING]: "Payer sent",
  [InvoiceStatus.PAID]: "Payer sent",
  [InvoiceStatus.DRAFT]: "Payer is going to send",
  [InvoiceStatus.CANCELLED]: "-",
};

const convertedAmountLabel = {
  [InvoiceStatus.PENDING]: "We will convert",
  [InvoiceStatus.PENDING_REVIEW]: "We will convert",
  [InvoiceStatus.PROCESSING]: "We converted",
  [InvoiceStatus.PAID]: "We converted",
  [InvoiceStatus.DRAFT]: "We will convert",
  [InvoiceStatus.CANCELLED]: "-",
};

const exchangeRateLabel = {
  [InvoiceStatus.PENDING]: "Exchange rate",
  [InvoiceStatus.PENDING_REVIEW]: "Exchange rate",
  [InvoiceStatus.PROCESSING]: "Exchange rate was",
  [InvoiceStatus.PAID]: "Exchange rate was",
  [InvoiceStatus.DRAFT]: "Exchange rate",
  [InvoiceStatus.CANCELLED]: "-",
};

const receivedAmountLabel = {
  [InvoiceStatus.PENDING]: "You will receive",
  [InvoiceStatus.PENDING_REVIEW]: "You will receive",
  [InvoiceStatus.PROCESSING]: "You have received",
  [InvoiceStatus.PAID]: "You have received",
  [InvoiceStatus.DRAFT]: "You will receive",
  [InvoiceStatus.CANCELLED]: "-",
};

export const TransactionDetails: FC = () => {
  const invoice = useSelector(selectInvoice);
  const { status, transaction_details } = invoice || {};
  const amount = transaction_details?.amount || 0;
  const commission = transaction_details?.commission_amount || 0;
  const converted = amount - commission;
  const invoiceCurrencies = useInvoiceCurrencies(invoice);
  const incomingCurrency = invoiceCurrencies?.incoming;

  const outgoingCurrency = transaction_details
    ? invoiceCurrencies?.outgoing
    : undefined;

  const paidAt =
    transaction_details && transaction_details?.paid_at
      ? formatDate(transaction_details?.paid_at, DateFormat.DD_MM_YYYY)
      : undefined;

  return (
    <div className="flex flex-col items-start">
      <div className="font-source-serif text-5xl mb-6">Transaction details</div>

      <div className="mb-16 bg-white px-8 pt-8 pb-8 rounded-2xl border border-gray-100 flex flex-col w-full md:w-1/2 gap-2">
        <div className="theme-caption mb-1 uppercase tracking-[1px]">
          Incoming Payment
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex">
            <div className="grow">{status ? paidAmountLabels[status] : ""}</div>

            <div className="grow text-right">
              {formatAmount(amount, incomingCurrency?.decimals)}{" "}
              {incomingCurrency?.symbol}
            </div>
          </div>

          <div className="flex">
            <div className="grow">Archway's fee</div>
            <div className="grow text-right">
              {formatAmount(commission, incomingCurrency?.decimals)}{" "}
              {incomingCurrency?.symbol}
            </div>
          </div>

          <div className="flex">
            <div className="grow">
              {status ? convertedAmountLabel[status] : ""}
            </div>

            <div className="grow text-right">
              {formatAmount(converted, incomingCurrency?.decimals)}{" "}
              {incomingCurrency?.symbol}
            </div>
          </div>

          <div className="flex">
            <div className="grow">
              {status ? exchangeRateLabel[status] : ""}
            </div>

            <div className="grow text-right">
              {formatAmount(transaction_details?.price || 0, 4)}{" "}
              {outgoingCurrency?.symbol}
            </div>
          </div>

          <div className="flex mt-4">
            <div className="grow font-bold">
              {status ? receivedAmountLabel[status] : ""}
            </div>

            <div className="grow text-right font-bold">
              {formatAmount(
                transaction_details?.outgoing_amount || 0,
                outgoingCurrency?.decimals
              )}{" "}
              {outgoingCurrency?.symbol}
            </div>
          </div>

          {paidAt && (
            <div className="flex mt-4 text-sm">
              <div className="grow">Paid at</div>

              <div className="grow text-right">{paidAt}</div>
            </div>
          )}

          {transaction_details?.explorer_link && (
            <div className="flex text-sm">
              <div className="grow">
                <a
                  href={transaction_details?.explorer_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  Go to blockchain explorer <Icon icon="New-Tab" size={16} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
